import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowRight, CheckCircle, Users, Clock, Award } from 'lucide-react';

export default function Home() {
  const { t } = useTranslation();

  const services = [
    { key: 'consultation', icon: '👨‍⚕️' },
    { key: 'endoscopy', icon: '🔬' },
    { key: 'waxRemoval', icon: '👂' },
    { key: 'microscope', icon: '🔍' },
    { key: 'foreignBody', icon: '🏥' },
    { key: 'allergyTesting', icon: '🧪' },
    { key: 'vertigo', icon: '⚖️' }
  ];

  const stats = [
    { icon: Users, label: 'Serving the Community', value: '30+' },
    { icon: Clock, label: t('about.experience'), value: '30+' },
    { icon: Award, label: 'Expert ENT Care', value: '100%' }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative grain-texture bg-secondary py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-tight mb-6" data-testid="hero-title">
                {t('hero.welcome')}
              </h1>
              <p className="text-base lg:text-lg text-muted-foreground mb-8 leading-relaxed" data-testid="hero-subtitle">
                {t('hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/appointment">
                  <Button size="lg" className="btn-primary rounded-full px-8 w-full sm:w-auto" data-testid="hero-cta-button">
                    {t('hero.cta')}
                    <ArrowRight size={20} className="ml-2" weight="bold" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button size="lg" variant="outline" className="rounded-full px-8 w-full sm:w-auto" data-testid="hero-learn-more-button">
                    {t('hero.learnMore')}
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="w-full h-96 bg-primary/10 rounded-3xl shadow-2xl flex items-center justify-center" data-testid="hero-placeholder">
                <div className="text-center p-8">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-primary flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">Dr</span>
                  </div>
                  <p className="text-xl font-semibold text-primary">Dr. N.C. Upadhyaya</p>
                  <p className="text-xs text-muted-foreground">MBBS, DLO</p>
                  <p className="text-xs text-muted-foreground">Chief ENT Consultant</p>
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-xl" data-testid="hero-stats-card">
                <p className="text-3xl font-bold text-primary">30+</p>
                <p className="text-sm text-muted-foreground">Years of Service</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-4" data-testid="services-section-title">
              {t('services.title')}
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto" data-testid="services-section-subtitle">
              {t('services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-8 card-hover border-border/50" data-testid={`service-card-${service.key}`}>
                  <div className="text-5xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    {t(`services.${service.key}.title`)}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t(`services.${service.key}.description`)}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="rounded-full" data-testid="view-all-services-button">
                View All Services
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-secondary/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="w-full h-96 bg-secondary rounded-3xl shadow-xl flex items-center justify-center" data-testid="about-section-placeholder">
                <div className="text-center p-8">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-3xl">🏥</span>
                  </div>
                  <p className="text-lg font-semibold text-primary">ENT Examination</p>
                  <p className="text-sm text-muted-foreground mt-2">Professional Care</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6" data-testid="about-section-title">
                {t('about.title')}
              </h2>
              <p className="text-sm lg:text-base text-muted-foreground leading-relaxed mb-8" data-testid="about-section-description">
                {t('about.description')}
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Over 30 years of specialized ENT experience',
                  'Advanced diagnostic equipment',
                  'Patient-centered care approach',
                  'Serving Biswanath and surrounding communities'
                ].map((point, index) => (
                  <div key={index} className="flex items-start gap-3" data-testid={`about-feature-${index}`}>
                    <CheckCircle size={24} className="text-primary flex-shrink-0 mt-0.5" fill="currentColor" />
                    <span className="text-sm text-foreground">{point}</span>
                  </div>
                ))}
              </div>

              <Link to="/about">
                <Button size="lg" className="rounded-full" data-testid="learn-more-about-doctor-button">
                  Learn More About Dr. Upadhyaya
                  <ArrowRight size={20} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`stat-card-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-secondary flex items-center justify-center">
                  <stat.icon size={32} className="text-primary" />
                </div>
                <p className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6" data-testid="cta-section-title">
            Ready to Schedule Your Consultation?
          </h2>
          <p className="text-base lg:text-lg text-white/80 mb-8 leading-relaxed" data-testid="cta-section-description">
            Book your appointment today and experience expert ENT care from Dr. N.C. Upadhyaya
          </p>
          <Link to="/appointment">
            <Button size="lg" className="bg-accent hover:bg-accent/90 text-white rounded-full px-8" data-testid="cta-book-appointment-button">
              Book Your Appointment Now
              <ArrowRight size={20} className="ml-2" weight="bold" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
